import { useQuery } from "@tanstack/react-query";
import { getMetrics } from "@/requests/internal-products/get-metrics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, CheckCircle, AlertCircle, TrendingUp, Clock, UserCheck } from "lucide-react";

export function MetricsDashboard() {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ["/api/internal/products/metrics"],
    queryFn: getMetrics,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive">Failed to load metrics</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {/* Total Products */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Products</CardTitle>
          <Package className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {metrics.totalProducts.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Products in master catalog
          </p>
        </CardContent>
      </Card>

      {/* Enriched Products */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Enriched Products
          </CardTitle>
          <CheckCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {metrics.enrichedProducts.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {metrics.enrichmentRate.toFixed(1)}% enrichment rate
          </p>
        </CardContent>
      </Card>

      {/* Enrichment Rate */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Enrichment Rate
          </CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {metrics.enrichmentRate.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            AI-generated descriptions
          </p>
        </CardContent>
      </Card>

      {/* Pending Enrichment */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Pending Enrichment</CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {metrics.pendingEnrichment.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Being processed automatically
          </p>
        </CardContent>
      </Card>

      {/* Needs Review */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Needs Review</CardTitle>
          <UserCheck className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {metrics.needsReview.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Requires human attention
          </p>
        </CardContent>
      </Card>

      {/* Pending Review (legacy - kept for context) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total in Queue</CardTitle>
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {metrics.pendingReview.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            All pending items
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
